<template>
  <div
    :style="{ 'z-index': getPopupZindex }"
    :class="{'confirm-overlay': true, 'is-visible': isVisible }">
    <div class="confirm">
      <a
        v-if="closeButton"
        href="#"
        class="confirm-close"
        @click.prevent="closePopup">
        &times;
      </a>

      <h2 v-if="title">
        {{ title }}
      </h2>

      <p
        v-if="desc && !allowPureHTML"
        v-pure-html="desc"></p>

      <p
        v-if="desc && allowPureHTML"
        v-html="desc"></p>

      <div
        v-if="input"
        class="confirm-input">
        <label>
          {{ inputLabel }}
        </label>
        <input
          slot="field"
          :type="inputType"
          :min="inputMin"
          :max="inputMax"
          :step="inputStep"
          v-model="inputValue" />
      </div>

      <div
        v-if="checkbox"
        class="confirm-switcher">
        <switcher
          ref="confirm-switcher"
          key="confirm-switcher"
          v-model="checkboxValue"
          slot="field" />
        <label>
          {{ checkboxLabel }}
        </label>
      </div>

      <btn
        @click.native="ok"
        :type="buttonType()">
        {{ okLabel }}
      </btn>

      <btn
        @click.native="cancel"
        type="popup-right">
        {{ cancelLabel }}
      </btn>
    </div>
  </div>
</template>

<script>
import PopupZindex from './../mixins/PopupZindex';

export default {
  name: 'confirm',
  mixins: [
    PopupZindex
  ],
  data () {
    return {
      isVisible: false,
      title: '',
      desc: '',
      okLabel: '',
      checkbox: false,
      checkboxLabel: '',
      checkboxValue: false,
      cancelLabel: '',
      okClick: false,
      isDanger: true,
      cancelClick: false,
      closeButton: false,
      allowPureHTML: false,
      input: false,
      inputValue: '',
      inputLabel: '',
      inputType: 'text',
      inputMin: null,
      inputMax: null,
      inputStep: null,
      hideOnRouteChange: false
    };
  },
  watch: {
    $route () {
      if (this.isVisible && this.hideOnRouteChange) {
        this.hide();
      }
    }
  },
  mounted () {
    this.$bus.$on('confirm-display', this.show);
  },
  methods: {
    ok () {
      if (this.okClick) {
        if (this.input && this.checkbox) {
          this.okClick(this.inputValue && this.checkboxValue);
        } else if (this.input) {
          this.okClick(this.inputValue);
        } else if (this.checkbox) {
          this.okClick(this.checkboxValue);
        } else {
          this.okClick();
        }
      }

      this.hide();
    },
    show (config) {
      this.isVisible = true;
      this.title = config.title || '';
      this.desc = config.desc || '';
      this.okLabel = config.okLabel || this.$t('common.ui.ok');
      this.cancelLabel = config.cancelLabel || this.$t('common.ui.cancel');
      this.okClick = config.okClick || false;
      this.cancelClick = config.cancelClick || false;
      this.closeButton = config.closeButton || false;
      this.checkbox = config.checkbox || false;
      this.checkboxLabel = config.checkboxLabel || '';
      this.input = config.input || false;
      this.inputLabel = config.inputLabel || '';
      this.inputMin = config.inputMin || null;
      this.inputMax = config.inputMax || null;
      this.inputStep = config.inputStep || null;
      this.inputType = config.inputType || 'text';
      this.allowPureHTML = config.allowPureHTML || false;
      this.hideOnRouteChange = config.hideOnRouteChange || false;

      if (typeof config.checkboxDefaultValue !== 'undefined') {
        this.checkboxValue = config.checkboxDefaultValue;
      }

      if (typeof config.inputDefaultValue !== 'undefined') {
        this.inputValue = config.inputDefaultValue;
      }

      if (typeof config.isDanger !== 'undefined') {
        this.isDanger = config.isDanger;
      } else {
        this.isDanger = true;
      }

      this.increasePopupZindex();
    },
    cancel () {
      if (this.cancelClick) {
        if (this.input && this.checkbox) {
          this.cancelClick(this.inputValue, this.checkboxValue);
        } else if (this.input) {
          this.cancelClick(this.inputValue);
        } else if (this.checkbox) {
          this.cancelClick(this.checkboxValue);
        } else {
          this.cancelClick();
        }
      }

      this.hide();
    },
    hide () {
      this.isVisible = false;
      this.removePopupZindex();
    },
    buttonType () {
      let type = ['popup-left'];

      if (this.isDanger) {
        type.push('danger');
      }

      if (!this.isDanger) {
        type.push('success');
      }

      return type.join(' ');
    },
    closePopup () {
      this.hide();
    }
  },
  beforeDestroy () {
    this.$bus.$off('confirm-display', this.show);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.confirm {
  background: $color-light;
  border-radius: $border-radius-small;
  box-shadow: 0 5px 5px rgba(0, 0, 0, .25);
  left: 50%;
  max-width: 94%;
  padding: 2rem 2rem 6rem 2rem;
  position: fixed;
  top: 50%;
  transform: translateX(-50%) translateY(-33.333333%);
  transition: transform .25s ease-out;
  width: 48rem;
  will-change: transform;

  &-overlay {
    background: rgba(0, 0, 0, .25);
    height: 100%;
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: fixed;
    top: 0;
    transition: opacity .25s ease-out;
    width: 100vw;
    will-change: opacity;

    &.is-visible {
      opacity: 1;
      pointer-events: auto;

      .confirm {
        transform: translateX(-50%) translateY(-50%);
      }
    }
  }

  &-switcher {
    align-items: center;
    display: flex;
    font-size: 14px;
    margin-bottom: 10px;

    .switcher {
      margin-right: 10px;
      min-width: 4rem;
    }
  }

  .confirm-input input {
    background: #FFF;
    border: 1px solid #DEE1E4;
    border-radius: 5px;
    font-size: 1.4rem;
    margin: 0 0 0 .5rem;
    padding: 1rem;
    text-align: center;
    width: 25%;
  }

  h2 {
    font-weight: 400;
    margin: 0 0 2rem 0;
  }

  p {
    color: $color-text;
  }

  .button {
    border-radius: 0 0 0 $border-radius-small;
    line-height: 1.1;
    white-space: normal;

    & + .button {
      border-radius: 0 0 $border-radius-small 0;
    }
  }

  &-close {
    background: $color-danger;
    border-radius: 50%;
    color: $color-light;
    font-size: 3.6rem;
    font-weight: bold;
    height: 3.6rem;
    line-height: 3.5rem;
    position: absolute;
    right: -1.8rem;
    text-align: center;
    top: -1.8rem;
    width: 3.6rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/Confirm.vue